import React, { useState } from 'react';
import { X, Building2, Users, Briefcase, Mail, User, Phone } from 'lucide-react';
import axios from 'axios';
import {
  getCountryPhoneCodes,
  DEFAULT_PHONE_COUNTRY,
  formatPhoneWithCountry
} from '../utils/phone';
import { useTranslation } from 'react-i18next';
import { apiErrorText } from '../utils/api';

interface EnterpriseRequestDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const EnterpriseRequestDialog: React.FC<EnterpriseRequestDialogProps> = ({ isOpen, onClose }) => {
  const { t, i18n } = useTranslation();
  const [formData, setFormData] = useState({
    company_name: '',
    contact_name: '',
    contact_email: '',
    contact_phone_country: DEFAULT_PHONE_COUNTRY,
    contact_phone_number: '',
    team_size: '',
    hiring_needs: '',
    message: '',
    requested_ai_interviews: '',
    requested_active_positions: '',
    requested_cv_analyses: '',
    requested_team_members: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const teamSizes = [
    { value: '10-50', labelKey: 'enterpriseDialog.teamSizes.size10_50' },
    { value: '51-200', labelKey: 'enterpriseDialog.teamSizes.size51_200' },
    { value: '201-500', labelKey: 'enterpriseDialog.teamSizes.size201_500' },
    { value: '500-1000', labelKey: 'enterpriseDialog.teamSizes.size500_1000' },
    { value: '1000+', labelKey: 'enterpriseDialog.teamSizes.size1000Plus' }
  ];

  const hiringNeeds = [
    { value: '5-20 hires / month', labelKey: 'enterpriseDialog.hiringNeeds.range5_20' },
    { value: '21-50 hires / month', labelKey: 'enterpriseDialog.hiringNeeds.range21_50' },
    { value: '50+ hires / month', labelKey: 'enterpriseDialog.hiringNeeds.range50plus' }
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const {
      company_name,
      contact_name,
      contact_email,
      contact_phone_number,
      contact_phone_country,
      team_size,
      hiring_needs,
      message,
      requested_ai_interviews,
      requested_active_positions,
      requested_cv_analyses,
      requested_team_members
    } = formData;

    const numericFields = [
      requested_ai_interviews,
      requested_active_positions,
      requested_cv_analyses,
      requested_team_members
    ].map((value) => Number(value));

    if (numericFields.some((value) => !Number.isFinite(value) || value <= 0)) {
      setError(t('enterpriseDialog.errors.positiveNumbers'));
      setLoading(false);
      return;
    }

    try {
      await axios.post('/api/enterprise-requests', {
        company_name,
        contact_name,
        contact_email,
        contact_phone: contact_phone_number
          ? formatPhoneWithCountry(contact_phone_country, contact_phone_number)
          : undefined,
        team_size: team_size || undefined,
        hiring_needs: hiring_needs || undefined,
        message: message || undefined,
        requested_ai_interviews: Number(requested_ai_interviews),
        requested_active_positions: Number(requested_active_positions),
        requested_cv_analyses: Number(requested_cv_analyses),
        requested_team_members: Number(requested_team_members)
      });
      setSuccess(true);
      setTimeout(() => {
        onClose();
        setSuccess(false);
        setFormData({
          company_name: '',
          contact_name: '',
          contact_email: '',
          contact_phone_country: DEFAULT_PHONE_COUNTRY,
          contact_phone_number: '',
          team_size: '',
          hiring_needs: '',
          message: '',
          requested_ai_interviews: '',
          requested_active_positions: '',
          requested_cv_analyses: '',
          requested_team_members: ''
        });
      }, 2000);
    } catch (err: any) {
      setError(apiErrorText(err.response?.data?.detail) || t('errors.tryAgain'));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{t('enterpriseDialog.title')}</h2>
            <p className="text-sm text-gray-600 mt-1">
              {t('enterpriseDialog.subtitle')}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X size={24} />
          </button>
        </div>

        {success && (
          <div className="mx-6 mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-green-800 font-medium">
              {t('enterpriseDialog.success')}
            </p>
          </div>
        )}

        {error && (
          <div className="mx-6 mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Building2 className="inline w-4 h-4 mr-1" />
              {t('enterpriseDialog.fields.companyName')}
            </label>
            <input
              type="text"
              required
              value={formData.company_name}
              onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
              className="input w-full"
              placeholder={t('enterpriseDialog.placeholders.companyName')}
            />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <User className="inline w-4 h-4 mr-1" />
                {t('enterpriseDialog.fields.contactPerson')}
              </label>
              <input
                type="text"
                required
                value={formData.contact_name}
                onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                className="input w-full"
                placeholder={t('enterpriseDialog.placeholders.contactName')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Mail className="inline w-4 h-4 mr-1" />
                {t('enterpriseDialog.fields.email')}
              </label>
              <input
                type="email"
                required
                value={formData.contact_email}
                onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                className="input w-full"
                placeholder={t('enterpriseDialog.placeholders.email')}
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Phone className="inline w-4 h-4 mr-1" />
                {t('enterpriseDialog.fields.phone')}
              </label>
              <div className="flex gap-3">
                <div className="w-32 flex-shrink-0">
                  <select
                    className="input w-full"
                    value={formData.contact_phone_country}
                    onChange={(e) => setFormData({ ...formData, contact_phone_country: e.target.value })}
                  >
                    {getCountryPhoneCodes(i18n.language).map((country) => (
                      <option key={country.code} value={country.code}>
                        {country.label}
                      </option>
                    ))}
                  </select>
                </div>
                <input
                  type="tel"
                  inputMode="tel"
                  pattern="\d{5,14}"
                  value={formData.contact_phone_number}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      contact_phone_number: e.target.value.replace(/[^\d]/g, ''),
                    })
                  }
                  className="input flex-1"
                  placeholder={t('enterpriseDialog.placeholders.phone')}
                />
              </div>
              {formData.contact_phone_number && (
                <p className="text-xs text-gray-500 mt-1">
                  {formatPhoneWithCountry(
                    formData.contact_phone_country,
                    formData.contact_phone_number
                  )}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Users className="inline w-4 h-4 mr-1" />
                {t('enterpriseDialog.fields.teamSize')}
              </label>
              <select
                value={formData.team_size}
                onChange={(e) => setFormData({ ...formData, team_size: e.target.value })}
                className="input w-full"
              >
                <option value="">{t('enterpriseDialog.placeholders.selectTeamSize')}</option>
                {teamSizes.map(({ value, labelKey }) => (
                  <option key={value} value={value}>
                    {t(labelKey)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Briefcase className="inline w-4 h-4 mr-1" />
              {t('enterpriseDialog.fields.hiringVolume')}
            </label>
            <select
              value={formData.hiring_needs}
              onChange={(e) => setFormData({ ...formData, hiring_needs: e.target.value })}
              className="input w-full"
            >
              <option value="">{t('enterpriseDialog.placeholders.selectVolume')}</option>
              {hiringNeeds.map(({ value, labelKey }) => (
                <option key={value} value={value}>
                  {t(labelKey)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('enterpriseDialog.fields.additionalContext')}
            </label>
            <textarea
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              className="input w-full"
              rows={4}
              placeholder={t('enterpriseDialog.placeholders.message')}
            />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('enterpriseDialog.fields.aiInterviews')}
              </label>
              <input
                type="number"
                min={1}
                required
                value={formData.requested_ai_interviews}
                onChange={(e) => setFormData({ ...formData, requested_ai_interviews: e.target.value })}
                className="input w-full"
                placeholder={t('enterpriseDialog.placeholders.aiInterviews')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('enterpriseDialog.fields.activePositions')}
              </label>
              <input
                type="number"
                min={1}
                required
                value={formData.requested_active_positions}
                onChange={(e) => setFormData({ ...formData, requested_active_positions: e.target.value })}
                className="input w-full"
                placeholder={t('enterpriseDialog.placeholders.activePositions')}
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('enterpriseDialog.fields.cvAnalyses')}
              </label>
              <input
                type="number"
                min={1}
                required
                value={formData.requested_cv_analyses}
                onChange={(e) => setFormData({ ...formData, requested_cv_analyses: e.target.value })}
                className="input w-full"
                placeholder={t('enterpriseDialog.placeholders.cvAnalyses')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('enterpriseDialog.fields.teamMembers')}
              </label>
              <input
                type="number"
                min={1}
                required
                value={formData.requested_team_members}
                onChange={(e) => setFormData({ ...formData, requested_team_members: e.target.value })}
                className="input w-full"
                placeholder={t('enterpriseDialog.placeholders.teamMembers')}
              />
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="btn-outline flex-1" disabled={loading}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn-primary flex-1" disabled={loading}>
              {loading ? t('enterpriseDialog.sending') : t('enterpriseDialog.send')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EnterpriseRequestDialog;
